import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  assertNever,
  ControlCode,
  ControlCodes,
  ControlMonitorEndpointPolicy,
  ControlMonitorInboxPolicy,
  ControlMonitorPolicySetting,
  ControlMonitorUserPolicy,
  cn,
  getControlName,
  isDefensiveControl,
  isEmailControl,
  isIdentityProviderControl,
  isTruthy,
  NonArchPlatform,
  queries,
  useAuthConfig,
  useDebounceState,
  ValidControlCode,
} from "@theprelude/core";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ExceptionIcon,
  PlatformIcon,
  RotatingChevron,
  ToggleGroup,
  ToggleItem,
  Truncate,
  useCollapsible,
} from "@theprelude/ds";
import { Suspense, useEffect, useMemo } from "react";
import { z } from "zod";
import { PolicySettingsDeepLink } from "@/components/deep-links";
import { DefaultOverlay } from "@/components/default-overlay";
import { Highlight } from "@/components/highlight";
import {
  policyArrowColumn,
  policySettingCriticalColumn,
  policySettingNameColumn,
  policySettingRecommendationColumn,
  policySettingTechniquesColumn,
  policySettingValueColumn,
} from "@/components/table/columns";
import { SCMTableFilterInput } from "@/components/table/filter-input";
import { useTable } from "@/components/table/hooks";
import { VirtualTable } from "@/components/table/virtual-table";
import { useOverlay } from "@/hooks/use-overlay";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { controlInstanceSchema } from "@/lib/validators";
import {
  getControlTopLevelCategory,
  ResourceType,
  useTopLevelFiltersQuery,
} from "@/queries/control-monitor";
import { Exception, exceptionsQuery } from "@/queries/exceptions";
import { SCMPolicyEmptyState } from "../endpoint-tables/scm-table-empty-state";
import { ExceptionCallout } from "../exception-callout";
import { ExportEndpointsButton } from "../export-endpoints-button";
import { ExportInboxesButton } from "../export-inboxes-button";
import { ExportUsersButton } from "../export-users-button";
import { PolicyStatus } from "../policy-status";

export const policiesSchema = z.tuple([
  z.literal("policies"),
  controlInstanceSchema.extend({
    filter: z.enum(["failing", "passing", "critical", "all"]),
  }),
]);

export type PoliciesSchema = z.infer<typeof policiesSchema>[1];

export type PolicyFilter = PoliciesSchema["filter"];

export type PolicySetting = ControlMonitorPolicySetting & {
  control: ControlCode;
  policyId: string;
  platform: NonArchPlatform | undefined;
};

function getResourceType(control: ValidControlCode): ResourceType {
  return isEmailControl(control)
    ? "inbox"
    : isIdentityProviderControl(control)
      ? "user"
      : "endpoint";
}

function PoliciesContent({ instanceId, control, filter }: PoliciesSchema) {
  return (
    <Misconfigurations
      instanceId={instanceId}
      control={control}
      filter={filter}
      resourceType={getResourceType(control)}
    />
  );
}

export const PoliciesOverlay = ({
  instanceId,
  control,
  filter,
}: PoliciesSchema) => {
  const navigate = useNavigate();
  const title = useTitle(control);

  return (
    <DefaultOverlay title={title}>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <ToggleGroup
            value={filter}
            onValueChange={(value) => {
              void navigate({
                to: window.location.pathname,
                params: (prev) => prev,
                search: (prev) => ({
                  ...prev,
                  overlay: ["policies", { instanceId, control, filter: value }],
                }),
              });
            }}
          >
            <ToggleItem value={"all" satisfies PolicyFilter}>
              All Configurations
            </ToggleItem>
            <ToggleItem value={"passing" satisfies PolicyFilter}>
              Passing
            </ToggleItem>
            <ToggleItem value={"failing" satisfies PolicyFilter}>
              Failing
            </ToggleItem>
            <ToggleItem value={"critical" satisfies PolicyFilter}>
              Critical
            </ToggleItem>
          </ToggleGroup>
        </div>
        <Suspense fallback={<PoliciesLoading />}>
          <div className="flex-1 overflow-hidden">
            <PoliciesContent
              instanceId={instanceId}
              control={control}
              filter={filter}
            />
          </div>
        </Suspense>
      </div>
    </DefaultOverlay>
  );
};

const useTitle = (control: ControlCode) => {
  const router = useRouterState();
  const authConfig = useAuthConfig();
  const technique = router.location.search.technique;
  const threatId = router.location.search.threat_id;
  const { data: threatQuery } = useSuspenseQuery({
    ...queries.scm.threats(authConfig),
    select: (threats) => threats.find((t) => t.id === threatId),
  });
  const controlName = getControlName(control);

  if (technique) {
    return `${controlName} Policy Configurations for ${technique}`;
  }
  if (threatQuery) {
    return `${controlName} Policy Configurations for ${threatQuery.name}`;
  }
  return `${controlName} Policy Configurations`;
};

type ControlMonitorPolicy =
  | ControlMonitorUserPolicy
  | ControlMonitorInboxPolicy
  | ControlMonitorEndpointPolicy;

type KeyedControlMonitorPolicySetting = PolicySetting & { key: string };

const getRowId = (row: KeyedControlMonitorPolicySetting): string => row.key;

const filterPolicySettings = ({
  settings,
  policyFilter,
  searchValue,
}: {
  settings: ControlMonitorPolicySetting[];
  policyFilter: PolicyFilter;
  searchValue: string;
}) => {
  const searchSettings = settings.filter((setting) =>
    fuzzyFilter(setting.name, searchValue),
  );

  switch (policyFilter) {
    case "all": {
      return searchSettings;
    }
    case "passing": {
      return searchSettings.filter((s) => s.meets_recommendation);
    }
    case "failing": {
      return searchSettings.filter(
        (s) => !s.meets_recommendation || s.excepted,
      );
    }
    case "critical": {
      return searchSettings.filter((s) => s.critical);
    }
    default: {
      return assertNever(policyFilter);
    }
  }
};

const filterPolicies = ({
  policies,
  policyFilter,
  searchValue,
}: {
  policies: ControlMonitorPolicy[];
  policyFilter: PolicyFilter;
  searchValue: string;
}) => {
  return policies
    .map((policy) => ({
      ...policy,
      settings: filterPolicySettings({
        settings: policy.settings,
        policyFilter,
        searchValue,
      }).sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter(
      (policy) =>
        fuzzyFilter(policy.name, searchValue) || policy.settings.length > 0,
    )
    .sort((a, b) => (getPolicyCount(a) < getPolicyCount(b) ? 1 : -1));
};

const getPolicyCount = (policy: ControlMonitorPolicy) => {
  if ("endpoint_count" in policy) {
    return policy.endpoint_count;
  }
  if ("user_count" in policy) {
    return policy.user_count;
  }
  if ("inbox_count" in policy) {
    return policy.inbox_count;
  }
  return assertNever(policy);
};

const isPlatformPolicy = (
  policy: ControlMonitorPolicy,
): policy is ControlMonitorEndpointPolicy => "platform" in policy;

interface MisconfigurationsProps {
  instanceId: string;
  control: ValidControlCode;
  filter: PolicyFilter;
  resourceType: ResourceType;
}

const Misconfigurations = (props: MisconfigurationsProps) => {
  const router = useRouterState();
  const threatId = router.location.search.threat_id;
  const technique = router.location.search.technique;

  if (technique) {
    return <TechniqueMisconfigurations {...props} technique={technique} />;
  }
  if (threatId) {
    return <ThreatMisconfigurations {...props} threatId={threatId} />;
  }
  return <PolicyMisconfigurations {...props} />;
};

interface ThreatMisconfigurationsProps extends MisconfigurationsProps {
  threatId: string;
}

function ThreatMisconfigurations({
  instanceId,
  control,
  filter,
  threatId,
  resourceType,
}: ThreatMisconfigurationsProps) {
  const authConfig = useAuthConfig();
  const { data: threat } = useSuspenseQuery({
    ...queries.scm.threats(authConfig),
    select: (threats) => threats.find((t) => t.id === threatId),
  });
  const techniques = threat?.techniques ?? [];
  const category = getControlTopLevelCategory(control);

  const topLevelFiltersQuery = useTopLevelFiltersQuery(category);
  const policiesQuery = useSuspenseQuery({
    ...queries.scm.policies(authConfig, control, instanceId, {
      techniques,
      query: topLevelFiltersQuery,
    }),
  });

  const policies = (
    policiesQuery.data[`${resourceType}_evaluation`]?.policies ?? []
  )
    .map((policy) => {
      return {
        ...policy,
        settings: policy.settings.map((setting) => {
          return {
            ...setting,
            techniques: setting.techniques.filter((technique) =>
              techniques.includes(technique),
            ),
          };
        }),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <PoliciesList
      instanceId={instanceId}
      control={control}
      filter={filter}
      policies={policies}
      resourceType={resourceType}
      showTechniques
    />
  );
}

function PolicyMisconfigurations({
  instanceId,
  control,
  filter,
  resourceType,
}: MisconfigurationsProps) {
  const authConfig = useAuthConfig();
  const category = getControlTopLevelCategory(control);

  const topLevelFiltersQuery = useTopLevelFiltersQuery(category);
  const policiesQuery = useSuspenseQuery({
    ...queries.scm.policies(authConfig, control, instanceId, {
      query: topLevelFiltersQuery,
    }),
  });

  const policies = (
    policiesQuery.data[`${resourceType}_evaluation`]?.policies ?? []
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <PoliciesList
      instanceId={instanceId}
      control={control}
      filter={filter}
      policies={policies}
      resourceType={resourceType}
      showTechniques={false}
    />
  );
}

interface TechniqueMisconfigurationsProps extends MisconfigurationsProps {
  technique: string;
}

function TechniqueMisconfigurations({
  instanceId,
  control,
  filter,
  technique,
  resourceType,
}: TechniqueMisconfigurationsProps) {
  const authConfig = useAuthConfig();
  const category = getControlTopLevelCategory(control);

  const topLevelFiltersQuery = useTopLevelFiltersQuery(category);
  const policiesQuery = useSuspenseQuery({
    ...queries.scm.policies(authConfig, control, instanceId, {
      techniques: [technique],
      query: topLevelFiltersQuery,
    }),
  });

  const policies = (
    policiesQuery.data[`${resourceType}_evaluation`]?.policies ?? []
  )
    .map((policy) => {
      return {
        ...policy,
        settings: policy.settings.map((setting) => {
          return {
            ...setting,
            techniques: [technique],
          };
        }),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <PoliciesList
      instanceId={instanceId}
      control={control}
      filter={filter}
      policies={policies}
      resourceType={resourceType}
      showTechniques
    />
  );
}

function PoliciesList<R extends ResourceType>({
  instanceId,
  control,
  filter: policyFilter,
  policies,
  showTechniques,
  resourceType,
}: {
  instanceId: string;
  control: ValidControlCode;
  filter: PolicyFilter;
  policies: ControlMonitorPolicy[];
  showTechniques: boolean;
  resourceType: R;
}) {
  const filter = useDebounceState("");
  const searchValue = filter.debouncedValue.toLowerCase();

  const filteredPolicies = useMemo(
    () => filterPolicies({ policies, policyFilter, searchValue }),
    [policies, policyFilter, searchValue],
  );

  return (
    <div className="flex flex-col gap-4">
      <SCMTableFilterInput
        value={filter.value}
        onChange={filter.setDelayed}
        onClear={() => filter.setImmediate("")}
      />
      {filteredPolicies.length === 0 ? (
        <SCMPolicyEmptyState />
      ) : (
        filteredPolicies.map((policy) => (
          <PolicyTable
            key={policy.id}
            policy={policy}
            instanceId={instanceId}
            control={control}
            showTechniques={showTechniques}
            searchValue={searchValue}
            resourceType={resourceType}
          />
        ))
      )}
    </div>
  );
}

function PolicyTable({
  instanceId,
  policy,
  control,
  showTechniques,
  searchValue,
  resourceType,
}: {
  policy: ControlMonitorPolicy;
  instanceId: string;
  control: ValidControlCode;
  showTechniques: boolean;
  searchValue: string;
  resourceType: ResourceType;
}) {
  const collapsible = useCollapsible();
  const overlay = useOverlay();
  const authConfig = useAuthConfig();

  const { data: exceptions } = exceptionsQuery(authConfig).useSuspenseQuery();
  const exception = exceptions.find(
    (e): e is Exception & { type: "policy" } =>
      e.type === "policy" &&
      e.policy_id === policy.id &&
      e.control === control &&
      e.instance_id === instanceId,
  );

  const query = useMemo(() => {
    return {
      filter: {
        instances: {
          any: {
            control,
            instance_id: instanceId,
            or: [{ policies: { eq: policy.id } }, { policies: { eq: "" } }],
          },
        },
      },
    };
  }, [control, instanceId, policy.id]);

  return (
    <div
      className={cn(!collapsible.open && "border-stroke-line border-b pb-4")}
    >
      <Collapsible {...collapsible}>
        <CollapsibleTrigger asChild>
          <div className="flex cursor-pointer items-center gap-2 text-lg">
            <RotatingChevron
              className="text-secondary-foreground"
              active={collapsible.open}
            />
            {isPlatformPolicy(policy) && (
              <PlatformIcon
                platform={policy.platform}
                className="text-secondary-foreground"
              />
            )}
            <div className="flex-1 overflow-hidden">
              <Truncate text={policy.name}>
                <Highlight text={policy.name} highlight={searchValue} />
              </Truncate>
            </div>
            <PolicyStatus
              resourceType={resourceType}
              resourceCount={getPolicyCount(policy)}
              misconfigurationCount={
                policy.settings.filter(
                  (s) => !s.meets_recommendation && !s.excepted,
                ).length ?? 0
              }
              exceptionCount={
                exceptions.filter(
                  (e) =>
                    e.type === "policy" &&
                    e.control === control &&
                    e.instance_id === instanceId &&
                    e.policy_id === policy.id,
                ).length
              }
            />
            <Button
              intent="pill"
              size="tiny"
              icon={<ExceptionIcon className="text-secondary-foreground" />}
              onClick={(e) => {
                e.stopPropagation();
                void overlay.open("add-policy-exception", {
                  instanceId,
                  control,
                  policyId: policy.id,
                });
              }}
            >
              {policy.settings.some((s) => s.excepted)
                ? "Edit Exception"
                : "Add Exception"}
            </Button>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {exception && (
            <ExceptionCallout
              exception={exception}
              instanceId={instanceId}
              control={control}
              policyId={policy.id}
            />
          )}
          <PolicySettingsTable
            policy={policy}
            control={control}
            showTechniques={showTechniques}
            searchValue={searchValue}
          />
          <div className="flex items-center justify-end gap-2 pt-4 pb-1">
            {resourceType === "endpoint" && (
              <ExportEndpointsButton control={control} query={query} />
            )}
            {resourceType === "inbox" && (
              <ExportInboxesButton control={control} query={query} />
            )}
            {resourceType === "user" && (
              <ExportUsersButton control={control} query={query} />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

const PolicySettingsTable = ({
  policy,
  control,
  showTechniques,
  searchValue,
}: {
  policy: ControlMonitorPolicy;
  control: ValidControlCode;
  showTechniques: boolean;
  searchValue: string;
}) => {
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<KeyedControlMonitorPolicySetting>();
    return [
      columnHelper.accessor("name", policySettingNameColumn),
      columnHelper.accessor("techniques", {
        ...policySettingTechniquesColumn,
        meta: {
          ...policySettingTechniquesColumn.meta,
          authorize: () => showTechniques,
        },
      }),
      isDefensiveControl(control) &&
        columnHelper.accessor("critical", policySettingCriticalColumn),
      columnHelper.accessor("value", policySettingValueColumn),
      columnHelper.accessor("id", policyArrowColumn),
      columnHelper.accessor(
        "recommendation",
        policySettingRecommendationColumn,
      ),
      columnHelper.display({
        id: "control",
        enableHiding: false,
        enableResizing: false,
        cell: (info) => {
          const isIntunePolicy =
            info.row.original.control === ControlCodes.ENTRA &&
            policy.name !== "User Settings";
          return (
            <div className="flex w-full justify-end">
              <PolicySettingsDeepLink
                control={
                  isIntunePolicy
                    ? ControlCodes.INTUNE
                    : info.row.original.control
                }
                policyId={info.row.original.policyId}
                platform={"platform" in policy ? policy.platform : undefined}
                settingId={info.row.original.id}
              >
                <Button intent="pill" size="tiny">
                  Modify
                </Button>
              </PolicySettingsDeepLink>
            </div>
          );
        },
        size: 70,
        meta: {
          label: " ",
          canExport: false,
        },
      }),
    ].filter(isTruthy);
  }, [control, policy, showTechniques]);

  const data: KeyedControlMonitorPolicySetting[] = useMemo(
    () =>
      policy.settings.map(
        (setting) =>
          ({
            key: `${setting.id}-${setting.name}`,
            policyId: policy.id,
            control,
            platform: isPlatformPolicy(policy) ? policy.platform : undefined,
            ...setting,
          }) satisfies KeyedControlMonitorPolicySetting,
      ),
    [control, policy],
  );

  const table = useTable({
    getRowId,
    name: "policy-settings",
    data,
    columns,
  });

  useEffect(() => {
    table.setGlobalFilter(searchValue);
  }, [searchValue, table]);

  return (
    <div className="mt-4 border-stroke-table border-b">
      <VirtualTable
        table={table}
        className="max-h-[calc(100vh-25rem)]"
        emptyState={<SCMPolicyEmptyState />}
      />
    </div>
  );
};

function PoliciesLoading() {
  return (
    <div className="flex h-full w-full flex-col">
      <div className="mt-4 flex flex-col gap-2 border-b border-b-stroke-line pb-4">
        <div className="my-1 h-3 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-1 h-3 w-1/3 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-4 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-4 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />
      </div>
      <div className="mt-4 flex flex-col gap-2 border-b border-b-stroke-line pb-4">
        <div className="my-1 h-3 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-1 h-3 w-1/3 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-4 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-4 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />
      </div>
      <div className="mt-4 flex flex-col gap-2 border-b border-b-stroke-line pb-4">
        <div className="my-1 h-3 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-1 h-3 w-1/3 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-4 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-4 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />
      </div>
    </div>
  );
}
